import type { ContactOrderPlanningPreview } from "../../../../../../api/kehrblatt/contact-orders"
import type {
  ContactOrderAppointmentRow,
  ContactOrderRecipientTag,
} from "../../../_logic/planning-selection-contract"
import {
  buildContactOrderAddressLines,
  buildContactOrderAppointmentTag,
  buildContactOrderPropertyTag,
  buildContactOrderSummaryTag,
  buildRecipientsForContactOrder,
  buildRecipientsFromPlanningPreview,
  formatContactOrderDate,
  formatContactOrderTimeRange,
  unitScopeForOrder,
  type ContactOrderTagContext,
} from "./tags"
import { customerInformationModeIsCompleted } from "../_shared/_logic/customer-information-state"

export type ContactOrderRowsInput = {
  plannedAppointments: ScheduleEvent[]
  createdOrderById: Record<string, WorkOrder>
  propertyById: Record<string, Property>
  employeeLabelById: Record<string, string>
  tagContext: ContactOrderTagContext
  planningPreviewByEventId: Record<string, ContactOrderPlanningPreview>
  planningPreviewErrorsByEventId: Record<string, string>
  dirtyScheduleEventIds: Record<string, true>
}

const hasPreviewRecipientProjection = (preview: ContactOrderPlanningPreview | null) =>
  Array.isArray(preview?.recipient_summaries)

const mergeRecipientCatalog = (
  baseRecipients: ContactOrderRecipientTag[],
  previewRecipients: ContactOrderRecipientTag[],
) => {
  const baseByCustomerId = new Map(
    baseRecipients.map((recipient) => [recipient.customerId, recipient]),
  )
  const previewCustomerIds = new Set(previewRecipients.map((recipient) => recipient.customerId))

  return [
    ...previewRecipients.map((recipient) => ({
      ...recipient,
      channelValues:
        baseByCustomerId.get(recipient.customerId)?.channelValues ?? recipient.channelValues,
    })),
    ...baseRecipients.filter((recipient) => !previewCustomerIds.has(recipient.customerId)),
  ]
}

const recipientsForContactOrderRow = (
  order: WorkOrder | null,
  tagContext: ContactOrderTagContext,
  planningPreview: ContactOrderPlanningPreview | null,
) => {
  const baseRecipients = buildRecipientsForContactOrder(order, tagContext)

  if (!hasPreviewRecipientProjection(planningPreview)) return baseRecipients

  return mergeRecipientCatalog(baseRecipients, buildRecipientsFromPlanningPreview(planningPreview))
}

export const buildContactOrderAppointmentRows = ({
  plannedAppointments,
  createdOrderById,
  propertyById,
  employeeLabelById,
  tagContext,
  planningPreviewByEventId,
  planningPreviewErrorsByEventId,
  dirtyScheduleEventIds,
}: ContactOrderRowsInput): ContactOrderAppointmentRow[] =>
  plannedAppointments.map((event) => {
    const order = event.work_order_id ? (createdOrderById[event.work_order_id] ?? null) : null
    const property = order?.property_id ? propertyById[order.property_id] : null
    const unitScope = unitScopeForOrder(order, property)
    const employee =
      event.employee_name ||
      (event.employee_id ? employeeLabelById[event.employee_id] : null) ||
      "—"
    const planningPreview = planningPreviewByEventId[event.id] ?? null
    const eventCustomerInformation = event as ScheduleEvent & {
      customer_information_mode?: string | null
      customer_information_label?: string | null
      customer_information_detail?: string | null
    }

    return {
      id: event.id,
      event,
      termin: formatContactOrderDate(event.start_at),
      timeLabel: formatContactOrderTimeRange(event.start_at, event.end_at),
      addressLines: buildContactOrderAddressLines(order, propertyById),
      employee,
      orderTag: buildContactOrderSummaryTag(
        order,
        property,
        event,
        employee,
        Boolean(dirtyScheduleEventIds[event.id]),
      ),
      appointmentTag: buildContactOrderAppointmentTag(event, employee),
      propertyTag: buildContactOrderPropertyTag(order, property, propertyById),
      recipients: recipientsForContactOrderRow(order, tagContext, planningPreview),
      unitScope,
      unitScopeLabel:
        unitScope === "RESIDENTIAL" ? "Wohneinheit / EFH" : "Mehrfamilienhaus / Gebäudeeinheit",
      planningPreview,
      planningPreviewError: planningPreviewErrorsByEventId[event.id] ?? null,
      customerInformationMode:
        planningPreview?.customer_information_mode ??
        eventCustomerInformation.customer_information_mode ??
        null,
      customerInformationLabel:
        planningPreview?.customer_information_label ??
        eventCustomerInformation.customer_information_label ??
        null,
      customerInformationDetail:
        planningPreview?.customer_information_detail ??
        eventCustomerInformation.customer_information_detail ??
        null,
      isDirty: Boolean(dirtyScheduleEventIds[event.id]),
    }
  })

export const buildContactOrderAppointmentsRefresh = (rows: ContactOrderAppointmentRow[]) =>
  rows
    .map((row) => {
      const source = row.event as ScheduleEvent & {
        customer_information_mode?: string | null
        customer_information_label?: string | null
        customer_information_detail?: string | null
      }

      return [
        row.id,
        row.event.start_at,
        row.event.end_at,
        row.event.flex_type ?? "",
        row.event.timeframe_start_at ?? "",
        row.event.timeframe_end_at ?? "",
        row.event.employee_id,
        row.event.work_order_id ?? "",
        row.event.contact_order_id ||
        customerInformationModeIsCompleted(row.customerInformationMode)
          ? "created"
          : "pending",
        source.customer_information_mode ?? "",
        source.customer_information_label ?? "",
        source.customer_information_detail ?? "",
        row.isDirty ? "dirty" : "clean",
      ].join(":")
    })
    .join("|")

export const filterPendingContactOrderRows = (rows: ContactOrderAppointmentRow[]) =>
  rows.filter(
    (row) =>
      !row.event.contact_order_id &&
      !customerInformationModeIsCompleted(row.customerInformationMode) &&
      Boolean(row.event.work_order_id),
  )
